import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ChevronRight, Home, Info, FileText, Mail, FileCheck, Globe, LogIn, Settings, LogOut, User } from 'lucide-react';
import { ART_DIRECTION_ROUTE, CONTACT_ROUTE, COSTUME_ROUTE, DOWNLOAD_FORM_ROUTE, HISTORY_ROUTE, HOME_ROUTE, LOGIN_ROUTE, MEMBER_DASHBOARD_ROUTE, NOTIFICATION_ROUTE, RULES_ROUTE, TRACK_ROUTE, TRIBUTE_ROUTE } from '../../routes/web/rootRoutes';

import { Link as LinkRouter, useNavigate } from 'react-router-dom';
import authManager from '../../utils/authManager';
import { getFileUrl } from '../../utils/helper';
import { useSelector } from 'react-redux';
import { Avatar, notification } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import LanguageToggle from './LanguageToggle';
import AppDownload from '../AppDownload';



const Link = ({ to, children, className, onClick }) => (
    <LinkRouter to={to} className={className} onClick={onClick}>
        {children}
    </LinkRouter>
);


const translations = {
    en: {
        home: 'Home',
        aboutUs: 'About Us',
        ourHistory: 'Our History',
        tributes: 'Tributes',
        livingLegends: 'Living Legends',
        artDirection: 'Art Direction',
        costumeDesigner: 'Costume Designer',
        rules: 'Blogs',
        forms: 'Forms',
        trackApplication: 'Track your Application',
        downloadForm: 'Download / Apply Form',
        contactUs: 'Contact Us',
        notification: 'Notification',
        login: 'Login',
        language: 'Language'
    },
    hi: {
        home: 'होम',
        aboutUs: 'हमारे बारे में',
        ourHistory: 'हमारा इतिहास',
        tributes: 'श्रद्धांजलि',
        livingLegends: 'जीवित किंवदंतियाँ',
        artDirection: 'कला निर्देशन',
        costumeDesigner: 'वेशभूषा डिजाइनर',
        rules: 'ब्लॉग',
        forms: 'फॉर्म',
        trackApplication: 'अपना आवेदन ट्रैक करें',
        downloadForm: 'फॉर्म डाउनलोड / आवेदन करें',
        contactUs: 'संपर्क करें',
        login: 'लॉगिन',
        language: 'भाषा'
    }
};


function Header() {
    const setting = useSelector((state) => state?.globalState?.siteSetting?.site_settings);
    const LOGO = getFileUrl(setting?.site_logo, "settings");
    const isLogin = authManager.getAccessToken();
    const isUser = authManager.getUserProfile();
    const navigate = useNavigate();

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState({});
    const [currentLang, setCurrentLang] = useState('en');
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);

    useEffect(() => {
        let intervalId = null;

        function applyTranslate() {
            const combo = document.querySelector(".goog-te-combo");
            if (!combo) return false;

            try {
                if (combo.value !== currentLang) {
                    combo.value = currentLang;
                    combo.dispatchEvent(new Event("change"));
                }
            } catch (err) {
                console.warn("Google Translate combo error:", err);
            }
            return true;
        }

        if (!applyTranslate()) {
            intervalId = setInterval(() => {
                if (applyTranslate()) clearInterval(intervalId);
            }, 500);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [currentLang]);



    const switchLanguage = (lang) => {
        // setCurrentLang(lang); // triggers useEffect for Google Translate
        // localStorage.setItem('lang', lang); // persist selection
    };

    const t = translations[currentLang];

    
    const mainMenuItems = [
        {
            key: 'home',
            label: t.home,
            path: HOME_ROUTE,
            icon: <Home size={18} />
        },
        {
            key: 'about',
            label: t.aboutUs,
            path: HISTORY_ROUTE,
            icon: <Info size={18} />,
            // children: [
            //     { key: 'history', label: t.ourHistory, path: HISTORY_ROUTE },
            //     { key: 'tributes', label: t.tributes, path: TRIBUTE_ROUTE },
            //     {
            //         key: 'who',
            //         label: t.livingLegends,
            //         children: [
            //             { key: 'list', label: t.artDirection, path: ART_DIRECTION_ROUTE },
            //             { key: 'future', label: t.costumeDesigner, path: COSTUME_ROUTE }
            //         ]
            //     },
            // ]
        },
        {
            key: 'rules',
            label: t.rules,
            path: RULES_ROUTE,
            icon: <FileText size={18} />
        },
        // {
        //     key: 'forms',
        //     label: t.forms,
        //     icon: <FileCheck size={18} />,
        //     children: [
        //         { key: 'track', label: t.trackApplication, path: TRACK_ROUTE },
        //         { key: 'download', label: t.downloadForm, path: DOWNLOAD_FORM_ROUTE },
        //     ]
        // },
        {
            key: 'Notification',
            label: t.notification,
            path: NOTIFICATION_ROUTE,
            icon: <Mail size={18} />
        }
        ,
        {
            key: 'contact',
            label: t.contactUs,
            path: CONTACT_ROUTE,
            icon: <Mail size={18} />
        }
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        if (isMobileMenuOpen) {
            setOpenSubmenu({});
        }
    };

    const toggleSubmenu = (key) => {
        setOpenSubmenu(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    useEffect(() => {
        if (showLangDropdown) {
            setShowUserDropdown(false)
        }
    }, [showLangDropdown])

    useEffect(() => {
        if (showUserDropdown) {
            setShowLangDropdown(false)
        }
    }, [showUserDropdown])

    const renderDesktopMenuItem = (item) => {
        if (item.path) {
            return (
                <Link
                    to={item.path}
                    className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 ease-in-out flex items-center gap-2 relative group"
                >
                    <span className="relative">
                        {item.label}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300 group-hover:w-full"></span>
                    </span>
                </Link>
            );
        }



        if (item.children) {
            return (
                <div className="relative group">
                    <button className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 ease-in-out flex items-center gap-2 group/btn">
                        <span className="relative">
                            {item.label}
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300 group-hover:w-full"></span>
                        </span>
                        <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                    </button>
                    <div className="absolute left-0 mt-2 w-64 bg-white shadow-xl rounded-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out transform translate-y-2 group-hover:translate-y-0 z-50 overflow-visible">
                        {item.children.map((child, index) => (
                            <div key={child.key} className="relative">
                                {child.path ? (
                                    <Link
                                        to={child.path}
                                        className={`block px-5 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 group/link ${index === 0 ? 'rounded-t-lg' : ''} ${index === item.children.length - 1 && !child.children ? 'rounded-b-lg' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span>{child.label}</span>
                                            <ChevronRight size={16} className="opacity-0 -translate-x-2 transition-all duration-200 group-hover/link:opacity-100 group-hover/link:translate-x-0" />
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="group/nested">
                                        <button className="w-full text-left px-5 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 flex items-center justify-between">
                                            <span>{child.label}</span>
                                            <ChevronRight size={16} className="transition-transform duration-300 group-hover/nested:translate-x-1" />
                                        </button>
                                        <div className="absolute left-full top-0 w-64 bg-white shadow-xl rounded-lg border border-gray-100 opacity-0 invisible group-hover/nested:opacity-100 group-hover/nested:visible transition-all duration-300 ease-in-out transform -translate-x-2 group-hover/nested:translate-x-1 ml-2 z-50">
                                            {child.children?.map((nested, nestedIndex) => (
                                                <Link
                                                    key={nested.key}
                                                    to={nested.path}
                                                    className={`block px-5 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 group/link ${nestedIndex === 0 ? 'rounded-t-lg' : ''} ${nestedIndex === child.children.length - 1 ? 'rounded-b-lg' : ''}`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span>{nested.label}</span>
                                                        <ChevronRight size={16} className="opacity-0 -translate-x-2 transition-all duration-200 group-hover/link:opacity-100 group-hover/link:translate-x-0" />
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
    };

    const renderMobileMenuItem = (item, depth = 0) => {
        const isOpen = openSubmenu[item.key];

        if (item.path) {
            return (
                <Link
                    key={item.key}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 border-l-4 border-transparent hover:border-blue-600 ${depth === 1 ? 'pl-8 bg-gray-50' : depth === 2 ? 'pl-12 bg-gray-100' : ''
                        }`}
                >
                    {depth === 0 && item.icon}
                    <span className="font-medium">{item.label}</span>
                </Link>
            );
        }

        if (item.children) {
            return (
                <div key={item.key}>
                    <button
                        onClick={() => toggleSubmenu(item.key)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 border-l-4 border-transparent hover:border-blue-600 ${depth === 1 ? 'pl-8 bg-gray-50' : depth === 2 ? 'pl-12 bg-gray-100' : ''
                            } ${isOpen ? 'bg-blue-50 text-blue-600 border-blue-600' : ''}`}
                    >
                        <div className="flex items-center gap-3">
                            {depth === 0 && item.icon}
                            <span className="font-medium">{item.label}</span>
                        </div>
                        <ChevronDown
                            size={18}
                            className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        {item.children.map((child) => renderMobileMenuItem(child, depth + 1))}
                    </div>
                </div>
            );
        }
    };
    return (
        <div>

            <nav className={`${isScrolled ? 'shadow-lg' : 'shadow-md'
                } bg-white transition-all duration-300 ${isScrolled ? 'fixed top-0 left-0 right-0 z-40' : 'relative'
                }`}>
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo - Left Side */}
                       <div className="flex justify-center items-center gap-2">
                         <Link to={HOME_ROUTE} className="flex items-center gap-3 group flex-shrink-0">
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-800 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                    <img src={LOGO} alt="Logo" />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-400 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl"></div>
                            </div>
                            <h1 className="hidden uppercase md:block font-bold text-lg bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                               {setting?.site_name}
                            </h1>
                        </Link>
                        <AppDownload/>
                       </div>

                        <div className="hidden lg:flex items-center space-x-2 flex-1 justify-center">
                            {mainMenuItems.map((item) => (
                                <div key={item.key}>
                                    {renderDesktopMenuItem(item)}
                                </div>
                            ))}
                        </div>

                        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
                            {/* Language Dropdown */}
                            <div className="relative">
                                {/* <button
                                    onClick={() => setShowLangDropdown(!showLangDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-purple-600 transition-all duration-300 border border-gray-200 hover:border-purple-300"
                                >
                                    <Globe size={18} />
                                    <span className="font-medium">{currentLang === 'en' ? 'EN' : 'हि'}</span>
                                    <ChevronDown size={16} className={`transition-transform duration-300 ${showLangDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showLangDropdown && (
                                    <div className="absolute right-0 mt-2 w-40 bg-white shadow-xl rounded-lg border border-gray-100 overflow-hidden z-50">
                                        <button
                                            onClick={() => switchLanguage('en')}
                                            className={`w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200 ${currentLang === 'en' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                        >
                                            English
                                        </button>
                                        <button
                                            onClick={() => switchLanguage('hi')}
                                            className={`w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200 ${currentLang === 'hi' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                        >
                                            हिन्दी
                                        </button>
                                    </div>
                                )} */}

                                <LanguageToggle />
                            </div>

                            {/* Language Dropdown */}

                            {
                                localStorage.getItem('useAccessToken') !== null &&
                                <div className="relative">
                                    <Avatar size={42} icon={<UserOutlined />} onClick={() => setShowUserDropdown(!showUserDropdown)} />
                                    {showUserDropdown && (
                                        <div className="absolute right-0 mt-2 w-40 bg-white shadow-xl rounded-lg border border-gray-100 overflow-hidden z-50">
                                            <button
                                                onClick={() => navigate('/member/dashboard')}
                                                className={`w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200`}
                                            >
                                                Account
                                            </button>
                                            <button
                                                onClick={() => { localStorage.clear(); navigate('/') }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200`}
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            }

                            {
                                (isUser && isLogin) ?
                                    ''
                                    :

                                    <Link
                                        to={LOGIN_ROUTE}
                                        className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-800 to-blue-600 text-white rounded-lg font-semibold hover:from-yellow-600 hover:to-red-600 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl"
                                    >
                                        <LogIn size={18} />
                                        <span>{t.login}</span>
                                    </Link>

                            }
                        </div>

                        {
                            isUser ?
                                <>
                                    <div className="flex sm:hidden flex-col items-center ">
                                        <p className='text-blue-900 uppercase  border-b-2 border-cyan-700 font-bold'>Welcome</p>
                                        <p className=' text-[16px] uppercase  font-bold '>  {isUser?.name}</p>
                                    </div>
                                </>
                                : <>
                                </>
                        }


                        <button
                            onClick={toggleMobileMenu}
                            className="lg:hidden p-2 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 relative group"
                        >
                            <div className="relative z-10">
                                {isMobileMenuOpen ? (
                                    <>
                                        <X size={28} className="text-blue-600 transition-transform duration-300" />
                                    </>
                                ) : (
                                    <>
                                        <div className="flex gap-8 items-center group-hover:text-blue-600 transition-colors duration-300">
                                            {
                                                (isUser && isLogin) ?
                                                    <>
                                                    </>
                                                    // <Link
                                                    //     to={MEMBER_DASHBOARD_ROUTE}
                                                    //     onClick={() => setIsMobileMenuOpen(false)}
                                                    //     className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md"
                                                    // >
                                                    //     {/* <LogIn size={18} /> */}
                                                    // </Link>
                                                    :
                                                    <Link
                                                        to={LOGIN_ROUTE}
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                        className="flex items-center justify-center gap-2 w-full px-3 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md"
                                                    >
                                                        <User size={18} />
                                                    </Link>
                                            }
                                            <div className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-gradient-to-r  from-blue-400 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md">

                                                <Menu size={18} className="text-white group-hover:text-blue-600 transition-colors duration-300" />
                                            </div>

                                        </div>
                                    </>
                                )}
                            </div>
                        </button>
                    </div>

                    <div className={`lg:hidden border-t border-gray-200 transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-screen opacity-100 pb-4' : 'max-h-0 opacity-0 overflow-hidden'
                        }`}>
                        {mainMenuItems.map((item) => renderMobileMenuItem(item))}

                        {
                            (isUser && isLogin) ?
                            <>
                                <Link
                                    to={MEMBER_DASHBOARD_ROUTE}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md"
                                >
                                    <LogIn size={18} />
                                    <span>Dashboard</span>
                                </Link>
                                 <button
                                                onClick={() => { localStorage.clear(); navigate('/') }}
                                                className={`flex my-2 items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-600 text-white rounded-lg font-semibold hover:from-gray-700 hover:to-gray-700 transition-all duration-300 shadow-md`}
                                            >
                                                Logout
                                            </button>
                            </>
                                :
                                <Link
                                    to={LOGIN_ROUTE}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md"
                                >
                                    <LogIn size={18} />
                                    <span>{t.login}</span>
                                </Link>
                        }
                        <div className="mt-4 px-4 space-y-3 border-t border-gray-200 pt-4">
                            <LanguageToggle />
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    )
}

export default Header
